import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Container,
  Paper,
  Typography,
  TextField,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  IconButton,
  CircularProgress,
  Card,
  CardContent,
  Grid,
  InputAdornment,
  MenuItem,
  Select,
  FormControl,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  Snackbar,
  Alert,
  Tooltip,
  Tabs,
  Tab,
} from '@mui/material';
import {
  Refresh as RefreshIcon,
  Search as SearchIcon,
  Logout as LogoutIcon,
  FileDownload as DownloadIcon,
  Delete as DeleteIcon,
  Lock as LockIcon,
  Person as PersonIcon,
  CheckCircleOutline,
  HourglassEmpty,
  EventNote,
  CalendarToday,
  LocalOffer as LocalOfferIcon,
} from '@mui/icons-material';
import PriceManagement from './PriceManagement';

const STATUS_COLORS = {
  Pending: { bg: '#FFF4E5', color: '#B76E00', border: '#FFE2B7' },
  Confirmed: { bg: '#E8F4FD', color: '#1877F2', border: '#B8DBFC' },
  Completed: { bg: '#EBF8F2', color: '#1B8057', border: '#C0ECD7' },
  Cancelled: { bg: '#FEECEB', color: '#D32F2F', border: '#FDBBB8' },
};

export default function AdminDashboard() {
  // Authentication State
  const [token, setToken] = useState(() => localStorage.getItem('zeal_admin_token') || '');
  const [adminUser, setAdminUser] = useState(() => localStorage.getItem('zeal_admin_user') || '');

  // Login form state
  const [usernameInput, setUsernameInput] = useState('');
  const [passwordInput, setPasswordInput] = useState('');
  const [loginLoading, setLoginLoading] = useState(false);
  const [loginError, setLoginError] = useState('');

  // Dashboard state
  const [bookings, setBookings] = useState([]);
  const [stats, setStats] = useState({ total: 0, today: 0, pending: 0, confirmed: 0 });
  const [loading, setLoading] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');

  // Delete dialog state
  const [deleteId, setDeleteId] = useState(null);
  const [deleteLoading, setDeleteLoading] = useState(false);

  // Active Tab state ('bookings' or 'pricing')
  const [activeTab, setActiveTab] = useState('bookings');

  // Snackbar feedback
  const [snack, setSnack] = useState({ open: false, message: '', severity: 'info' });

  const showSnack = (message, severity = 'info') => {
    setSnack({ open: true, message, severity });
  };

  // -------------------------------------------------------------
  // Admin Login Action
  // -------------------------------------------------------------
  const handleLogin = async (e) => {
    e.preventDefault();
    setLoginError('');
    setLoginLoading(true);

    try {
      const res = await fetch('/api/admin_login.php', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          username: usernameInput,
          password: passwordInput,
        }),
      });

      const data = await res.json();

      if (res.ok && data.status === 'success') {
        localStorage.setItem('zeal_admin_token', data.token);
        localStorage.setItem('zeal_admin_user', data.username);
        setToken(data.token);
        setAdminUser(data.username);
        showSnack(`Welcome back, ${data.username}!`, 'success');
      } else {
        setLoginError(data.message || 'Invalid username or password');
      }
    } catch (err) {
      setLoginError('Network error connecting to /api/admin_login.php');
    } finally {
      setLoginLoading(false);
    }
  };

  // -------------------------------------------------------------
  // Logout Action
  // -------------------------------------------------------------
  const handleLogout = () => {
    localStorage.removeItem('zeal_admin_token');
    localStorage.removeItem('zeal_admin_user');
    setToken('');
    setAdminUser('');
    setBookings([]);
  };

  // -------------------------------------------------------------
  // Fetch Bookings from API
  // -------------------------------------------------------------
  const fetchBookings = useCallback(async () => {
    if (!token) return;
    setLoading(true);

    try {
      const params = new URLSearchParams();
      if (searchQuery) params.append('search', searchQuery);
      if (statusFilter && statusFilter !== 'all') params.append('status', statusFilter);

      const res = await fetch(`/api/get_bookings.php?${params.toString()}`, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      const data = await res.json();

      if (res.status === 401) {
        handleLogout();
        showSnack('Session expired. Please log in again.', 'warning');
        return;
      }

      if (res.ok && data.status === 'success') {
        setBookings(data.bookings || []);
        if (data.stats) setStats(data.stats);
      } else {
        showSnack(data.message || 'Failed to fetch bookings', 'error');
      }
    } catch (err) {
      showSnack('Network error fetching bookings', 'error');
    } finally {
      setLoading(false);
    }
  }, [token, searchQuery, statusFilter]);

  useEffect(() => {
    if (token) {
      fetchBookings();
    }
  }, [token, fetchBookings]);

  // -------------------------------------------------------------
  // Update Booking Status
  // -------------------------------------------------------------
  const handleStatusChange = async (id, newStatus) => {
    try {
      const res = await fetch('/api/update_booking.php', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({
          action: 'update_status',
          id,
          status: newStatus,
        }),
      });

      const data = await res.json();

      if (res.ok && data.status === 'success') {
        showSnack(`Booking #${id} status changed to ${newStatus}`, 'success');
        setBookings((prev) =>
          prev.map((item) => (item.id === id ? { ...item, status: newStatus } : item))
        );
      } else {
        showSnack(data.message || 'Failed to update status', 'error');
      }
    } catch (err) {
      showSnack('Network error updating status', 'error');
    }
  };

  // -------------------------------------------------------------
  // Delete Booking
  // -------------------------------------------------------------
  const confirmDelete = async () => {
    if (!deleteId) return;
    setDeleteLoading(true);

    try {
      const res = await fetch('/api/update_booking.php', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({
          action: 'delete',
          id: deleteId,
        }),
      });

      const data = await res.json();

      if (res.ok && data.status === 'success') {
        showSnack(`Booking #${deleteId} deleted`, 'success');
        setBookings((prev) => prev.filter((item) => item.id !== deleteId));
        setDeleteId(null);
      } else {
        showSnack(data.message || 'Failed to delete booking', 'error');
      }
    } catch (err) {
      showSnack('Network error deleting booking', 'error');
    } finally {
      setDeleteLoading(false);
    }
  };

  // -------------------------------------------------------------
  // Export Bookings to CSV
  // -------------------------------------------------------------
  const exportToCSV = () => {
    if (bookings.length === 0) {
      showSnack('No bookings to export', 'warning');
      return;
    }

    const headers = ['ID', 'Name', 'Phone', 'Email', 'Address', 'Booking Date', 'Time Slot', 'Status', 'Created At'];
    const rows = bookings.map((b) => [
      `"${b.id}"`,
      `"${b.name || ''}"`,
      `"${b.phone || ''}"`,
      `"${b.email || ''}"`,
      `"${(b.address || '').replace(/"/g, '""')}"`,
      `"${b.booking_date || ''}"`,
      `"${b.booking_time || ''}"`,
      `"${b.status || 'Pending'}"`,
      `"${b.created_at || ''}"`,
    ]);

    const csvContent = `data:text/csv;charset=utf-8,${[headers.join(','), ...rows.map((r) => r.join(','))].join('\n')}`;
    const encodedUri = encodeURI(csvContent);
    const link = document.createElement('a');
    link.setAttribute('href', encodedUri);
    link.setAttribute('download', `zeal_bookings_${new Date().toISOString().slice(0, 10)}.csv`);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    showSnack('CSV export downloaded successfully', 'success');
  };

  // -------------------------------------------------------------
  // Render Table Rows (Avoids nested JSX ternary)
  // -------------------------------------------------------------
  const renderTableBody = () => {
    if (loading && bookings.length === 0) {
      return (
        <TableRow>
          <TableCell colSpan={8} align="center" sx={{ py: 6 }}>
            <CircularProgress size={36} color="primary" />
            <Typography variant="body2" color="text.secondary" mt={1}>
              Loading bookings from database...
            </Typography>
          </TableCell>
        </TableRow>
      );
    }

    if (bookings.length === 0) {
      return (
        <TableRow>
          <TableCell colSpan={8} align="center" sx={{ py: 6 }}>
            <Typography variant="body1" fontWeight={600} color="text.secondary">
              No bookings found.
            </Typography>
            <Typography variant="caption" color="text.secondary">
              When customers schedule a home visit on your website, they will show up here.
            </Typography>
          </TableCell>
        </TableRow>
      );
    }

    return bookings.map((booking) => {
      const statusStyle = STATUS_COLORS[booking.status] || STATUS_COLORS.Pending;
      return (
        <TableRow key={booking.id} hover>
          <TableCell sx={{ fontWeight: 700 }}>#{booking.id}</TableCell>
          <TableCell>
            <Typography variant="body2" fontWeight={700} color="#111827">
              {booking.name}
            </Typography>
          </TableCell>
          <TableCell>
            <Typography variant="body2" fontWeight={600}>
              <a href={`tel:${booking.phone}`} style={{ color: '#ec2927', textDecoration: 'none' }}>
                {booking.phone}
              </a>
            </Typography>
            <Typography variant="caption" color="text.secondary">
              {booking.email}
            </Typography>
          </TableCell>
          <TableCell>
            <Typography variant="body2" fontWeight={600}>
              {booking.booking_date || 'N/A'}
            </Typography>
            <Typography variant="caption" color="text.secondary">
              Slot: {booking.booking_time || 'Not selected'}
            </Typography>
          </TableCell>
          <TableCell sx={{ maxWidth: 220 }}>
            <Typography
              variant="body2"
              sx={{
                whiteSpace: 'pre-wrap',
                wordBreak: 'break-word',
                fontSize: '13px',
              }}
            >
              {booking.address}
            </Typography>
          </TableCell>
          <TableCell>
            <FormControl size="small">
              <Select
                value={booking.status || 'Pending'}
                onChange={(e) => handleStatusChange(booking.id, e.target.value)}
                sx={{
                  fontSize: '12px',
                  fontWeight: 600,
                  borderRadius: 2,
                  backgroundColor: statusStyle.bg,
                  color: statusStyle.color,
                  border: `1px solid ${statusStyle.border}`,
                  '& .MuiSelect-select': { py: 0.6, px: 1.5 },
                  '& fieldset': { border: 'none' },
                }}
              >
                <MenuItem value="Pending">Pending</MenuItem>
                <MenuItem value="Confirmed">Confirmed</MenuItem>
                <MenuItem value="Completed">Completed</MenuItem>
                <MenuItem value="Cancelled">Cancelled</MenuItem>
              </Select>
            </FormControl>
          </TableCell>
          <TableCell>
            <Typography variant="caption" color="text.secondary">
              {booking.created_at ? new Date(booking.created_at).toLocaleString() : 'N/A'}
            </Typography>
          </TableCell>
          <TableCell align="center">
            <Tooltip title="Delete Booking">
              <IconButton size="small" color="error" onClick={() => setDeleteId(booking.id)}>
                <DeleteIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          </TableCell>
        </TableRow>
      );
    });
  };

  // =============================================================
  // Render: LOGIN SCREEN
  // =============================================================
  if (!token) {
    return (
      <Box
        sx={{
          minHeight: '100vh',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          background: 'linear-gradient(135deg, #1f1c2c 0%, #928DAB 100%)',
          p: 2,
        }}
      >
        <Paper
          elevation={8}
          sx={{
            maxWidth: 420,
            width: '100%',
            p: 4,
            borderRadius: 3,
            backgroundColor: '#ffffff',
          }}
        >
          <Box textAlign="center" mb={3}>
            <Typography variant="h5" fontWeight={700} color="#ec2927" letterSpacing={0.5}>
              ZEAL CURTAINS
            </Typography>
            <Typography variant="subtitle2" color="text.secondary" mt={0.5}>
              Admin Portal • Bookings Management
            </Typography>
          </Box>

          {loginError && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {loginError}
            </Alert>
          )}

          <form onSubmit={handleLogin}>
            <Box mb={2}>
              <TextField
                fullWidth
                size="medium"
                label="Username"
                value={usernameInput}
                onChange={(e) => setUsernameInput(e.target.value)}
                required
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <PersonIcon color="action" />
                    </InputAdornment>
                  ),
                }}
              />
            </Box>

            <Box mb={3}>
              <TextField
                fullWidth
                size="medium"
                type="password"
                label="Password"
                value={passwordInput}
                onChange={(e) => setPasswordInput(e.target.value)}
                required
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <LockIcon color="action" />
                    </InputAdornment>
                  ),
                }}
              />
            </Box>

            <Button
              type="submit"
              fullWidth
              variant="contained"
              size="large"
              disabled={loginLoading}
              sx={{
                py: 1.3,
                fontSize: '16px',
                fontWeight: 600,
                backgroundColor: '#ec2927',
                borderRadius: 2,
                '&:hover': { backgroundColor: '#d02524' },
              }}
            >
              {loginLoading ? <CircularProgress size={24} color="inherit" /> : 'Log In to Admin'}
            </Button>
          </form>
        </Paper>
      </Box>
    );
  }

  // =============================================================
  // Render: ADMIN DASHBOARD
  // =============================================================
  return (
    <Box sx={{ minHeight: '100vh', backgroundColor: '#F8F9FA', pb: 6 }}>
      {/* Top Navigation Bar */}
      <Box
        sx={{
          backgroundColor: '#ffffff',
          borderBottom: '1px solid #E5E7EB',
          py: 2,
          px: { xs: 2, md: 4 },
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
        }}
      >
        <Box display="flex" alignItems="center" gap={1.5}>
          <Typography variant="h6" fontWeight={800} color="#ec2927">
            ZEAL CURTAINS
          </Typography>
          <Chip label="Admin Portal" size="small" color="default" sx={{ fontWeight: 600 }} />
        </Box>

        <Box display="flex" alignItems="center" gap={2}>
          <Typography variant="body2" color="text.secondary">
            Logged in as: <b>{adminUser}</b>
          </Typography>
          <Button
            variant="outlined"
            size="small"
            color="error"
            startIcon={<LogoutIcon />}
            onClick={handleLogout}
            sx={{ borderRadius: 2 }}
          >
            Logout
          </Button>
        </Box>
      </Box>

      {/* Navigation Tabs */}
      <Box
        sx={{
          backgroundColor: '#ffffff',
          borderBottom: '1px solid #E5E7EB',
          px: { xs: 2, md: 4 },
        }}
      >
        <Tabs
          value={activeTab}
          onChange={(e, val) => setActiveTab(val)}
          sx={{
            '& .MuiTab-root': {
              textTransform: 'none',
              fontWeight: 600,
              fontSize: '15px',
              minHeight: 52,
            },
            '& .Mui-selected': {
              color: '#ec2927 !important',
            },
            '& .MuiTabs-indicator': {
              backgroundColor: '#ec2927',
              height: 3,
            },
          }}
        >
          <Tab
            value="bookings"
            icon={<EventNote fontSize="small" />}
            iconPosition="start"
            label={`Bookings & Leads (${stats.total || bookings.length})`}
          />
          <Tab
            value="pricing"
            icon={<LocalOfferIcon fontSize="small" />}
            iconPosition="start"
            label="Curtain & Blind Pricing"
          />
        </Tabs>
      </Box>

      <Container maxWidth="xl" sx={{ mt: 3.5 }}>
        {activeTab === 'pricing' ? (
          <PriceManagement token={token} showSnack={showSnack} />
        ) : (
          <>
            {/* KPI Cards */}
        <Grid container spacing={2.5} mb={3}>
          <Grid item xs={12} sm={6} md={3}>
            <Card elevation={0} sx={{ border: '1px solid #E5E7EB', borderRadius: 3 }}>
              <CardContent sx={{ display: 'flex', alignItems: 'center', gap: 2, py: 2.5 }}>
                <Box
                  sx={{
                    width: 50,
                    height: 50,
                    borderRadius: 2.5,
                    backgroundColor: '#FEECEB',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    color: '#ec2927',
                  }}
                >
                  <EventNote fontSize="medium" />
                </Box>
                <Box>
                  <Typography variant="h4" fontWeight={800} color="#111827">
                    {stats.total}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Total Bookings
                  </Typography>
                </Box>
              </CardContent>
            </Card>
          </Grid>

          <Grid item xs={12} sm={6} md={3}>
            <Card elevation={0} sx={{ border: '1px solid #E5E7EB', borderRadius: 3 }}>
              <CardContent sx={{ display: 'flex', alignItems: 'center', gap: 2, py: 2.5 }}>
                <Box
                  sx={{
                    width: 50,
                    height: 50,
                    borderRadius: 2.5,
                    backgroundColor: '#E8F4FD',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    color: '#1877F2',
                  }}
                >
                  <CalendarToday fontSize="medium" />
                </Box>
                <Box>
                  <Typography variant="h4" fontWeight={800} color="#111827">
                    {stats.today}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    New Today
                  </Typography>
                </Box>
              </CardContent>
            </Card>
          </Grid>

          <Grid item xs={12} sm={6} md={3}>
            <Card elevation={0} sx={{ border: '1px solid #E5E7EB', borderRadius: 3 }}>
              <CardContent sx={{ display: 'flex', alignItems: 'center', gap: 2, py: 2.5 }}>
                <Box
                  sx={{
                    width: 50,
                    height: 50,
                    borderRadius: 2.5,
                    backgroundColor: '#FFF4E5',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    color: '#B76E00',
                  }}
                >
                  <HourglassEmpty fontSize="medium" />
                </Box>
                <Box>
                  <Typography variant="h4" fontWeight={800} color="#111827">
                    {stats.pending}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Pending
                  </Typography>
                </Box>
              </CardContent>
            </Card>
          </Grid>

          <Grid item xs={12} sm={6} md={3}>
            <Card elevation={0} sx={{ border: '1px solid #E5E7EB', borderRadius: 3 }}>
              <CardContent sx={{ display: 'flex', alignItems: 'center', gap: 2, py: 2.5 }}>
                <Box
                  sx={{
                    width: 50,
                    height: 50,
                    borderRadius: 2.5,
                    backgroundColor: '#EBF8F2',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    color: '#1B8057',
                  }}
                >
                  <CheckCircleOutline fontSize="medium" />
                </Box>
                <Box>
                  <Typography variant="h4" fontWeight={800} color="#111827">
                    {stats.confirmed}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Confirmed
                  </Typography>
                </Box>
              </CardContent>
            </Card>
          </Grid>
        </Grid>

        {/* Toolbar & Filters */}
        <Paper
          elevation={0}
          sx={{
            p: 2.5,
            mb: 3,
            border: '1px solid #E5E7EB',
            borderRadius: 3,
            display: 'flex',
            flexWrap: 'wrap',
            gap: 2,
            alignItems: 'center',
            justifyContent: 'space-between',
          }}
        >
          <Box display="flex" flexWrap="wrap" gap={2} alignItems="center" flex={1}>
            <TextField
              size="small"
              placeholder="Search by name, phone, email..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              sx={{ minWidth: 280 }}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon fontSize="small" color="action" />
                  </InputAdornment>
                ),
              }}
            />

            <FormControl size="small" sx={{ minWidth: 160 }}>
              <Select
                value={statusFilter}
                onChange={(e) => setStatusFilter(e.target.value)}
                displayEmpty
              >
                <MenuItem value="all">All Statuses</MenuItem>
                <MenuItem value="Pending">Pending</MenuItem>
                <MenuItem value="Confirmed">Confirmed</MenuItem>
                <MenuItem value="Completed">Completed</MenuItem>
                <MenuItem value="Cancelled">Cancelled</MenuItem>
              </Select>
            </FormControl>

            <Button
              variant="outlined"
              size="medium"
              startIcon={<RefreshIcon />}
              onClick={fetchBookings}
              disabled={loading}
              sx={{ borderRadius: 2 }}
            >
              {loading ? 'Refreshing...' : 'Refresh'}
            </Button>
          </Box>

          <Button
            variant="contained"
            size="medium"
            startIcon={<DownloadIcon />}
            onClick={exportToCSV}
            sx={{
              backgroundColor: '#111827',
              color: '#ffffff',
              borderRadius: 2,
              '&:hover': { backgroundColor: '#1F2937' },
            }}
          >
            Export to CSV
          </Button>
        </Paper>

        {/* Bookings Table */}
        <Paper elevation={0} sx={{ border: '1px solid #E5E7EB', borderRadius: 3, overflow: 'hidden' }}>
          <TableContainer>
            <Table>
              <TableHead sx={{ backgroundColor: '#F9FAFB' }}>
                <TableRow>
                  <TableCell sx={{ fontWeight: 700, color: '#4B5563', width: 60 }}>#ID</TableCell>
                  <TableCell sx={{ fontWeight: 700, color: '#4B5563' }}>Customer</TableCell>
                  <TableCell sx={{ fontWeight: 700, color: '#4B5563' }}>Contact Info</TableCell>
                  <TableCell sx={{ fontWeight: 700, color: '#4B5563' }}>Visit Date & Slot</TableCell>
                  <TableCell sx={{ fontWeight: 700, color: '#4B5563' }}>Address</TableCell>
                  <TableCell sx={{ fontWeight: 700, color: '#4B5563' }}>Status</TableCell>
                  <TableCell sx={{ fontWeight: 700, color: '#4B5563' }}>Booked At</TableCell>
                  <TableCell sx={{ fontWeight: 700, color: '#4B5563', textAlign: 'center' }}>
                    Actions
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {renderTableBody()}
              </TableBody>
            </Table>
          </TableContainer>
        </Paper>
          </>
        )}
      </Container>

      {/* Delete Confirmation Modal */}
      <Dialog open={Boolean(deleteId)} onClose={() => setDeleteId(null)}>
        <DialogTitle>Confirm Delete Booking</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Are you sure you want to permanently delete booking #{deleteId}? This action cannot be
            undone.
          </DialogContentText>
        </DialogContent>
        <DialogActions sx={{ p: 2 }}>
          <Button onClick={() => setDeleteId(null)} disabled={deleteLoading}>
            Cancel
          </Button>
          <Button
            onClick={confirmDelete}
            color="error"
            variant="contained"
            disabled={deleteLoading}
          >
            {deleteLoading ? 'Deleting...' : 'Delete'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Feedback Snackbar */}
      <Snackbar
        open={snack.open}
        autoHideDuration={4000}
        onClose={() => setSnack((prev) => ({ ...prev, open: false }))}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert
          severity={snack.severity}
          onClose={() => setSnack((prev) => ({ ...prev, open: false }))}
          sx={{ width: '100%', boxShadow: 3 }}
        >
          {snack.message}
        </Alert>
      </Snackbar>
    </Box>
  );
}
